import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { RowSelectedEvent } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AssemblyGroupItem, AssemblyGroupService } from '@app/services/assembly-group.service';
import { DocumentItem, DocumentService } from '@app/services/document.service';
import { GetProductBomParams, ProductBomGet, ProductBomItem, ProductBomOwnKPIItem, ProductBomProductsUsedInItem, ProductBomSelectedProduct, ProductBomService } from '@app/services/product-bom.service';
import { ProductService, ProductVersionItem } from '@app/services/product.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-product-bom',
    standalone: false,
    templateUrl: './product-bom.component.html',
    styleUrls: ['./product-bom.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductBomComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetProductBomParams;

    public page?: ProductBomGet & ProductBomSelectedProduct;

    public productImageUrl: string = '';

    public customKpis: ProductBomOwnKPIItem[] = [];

    public assemblyGroups: AssemblyGroupItem[] = [];

    public productBomTree: ProductBomItem[] = [];

    public selectedProductBom?: ProductBomItem;

    public selectedProductBomNodeId: string = '';

    public usedInProducts: ProductBomProductsUsedInItem[] = [];

    public documents: DocumentItem[] = [];

    public pictures: DocumentItem[] = [];

    public videos: DocumentItem[] = [];

    public versions: ProductVersionItem[] = [];

    private loadedListTabs = new Set<ProductBomListTab>();

    public componentsGrid?: GridOptions;

    public operationsGrid?: GridOptions;

    public productVersionsGrid?: GridOptions<ProductVersionRow>;

    public productVersionProdplaceGrid?: GridOptions;

    public selectedProductVersionKeyno: number | null = null;

    private readonly destroy$ = new Subject<void>();

    private readonly selectedProductVersion$ = new Subject<string | number | null>();

    public errorCheckRunning: boolean = false;

    public get showProductInfo(): boolean {
        return !!this.page?.prod_id_selected || !!this.page?.prod_name_selected;
    }

    public get showAssemblyGroup(): boolean {
        return this.customKpis.length > 0 || this.page?.setting_show_assemblygroup_select_list === '1';
    }

    public get showButtonFunctions(): boolean {
        return this.page?.show_button_move_choice === '1' || this.page?.show_button_errorcheck === '1';
    }

    public get headerDividerRememberId(): string {
        const panels = [this.showProductInfo ? 'info' : '', 'image', this.showAssemblyGroup ? 'assembly-group' : '', this.showButtonFunctions ? 'button-functions' : ''].filter((panel) => !!panel);

        return `w_productbom.dividers.${panels.join('.')}`;
    }

    public get showEmptyListToast(): boolean {
        const selectedTab = this.page?.gb_productbom_list as ProductBomListTab | undefined;

        if (!selectedTab || !this.loadedListTabs.has(selectedTab)) return false;

        switch (selectedTab) {
            case 'OTHER':
                return this.usedInProducts.length === 0;
            case 'DOCUMENTS':
                return this.documents.length === 0;
            case 'PICTURES':
                return this.pictures.length === 0;
            case 'VIDEOS':
                return this.videos.length === 0;
            default:
                return false;
        }
    }

    constructor(
        private productBom: ProductBomService,
        private product: ProductService,
        private assemblyGroup: AssemblyGroupService,
        private document: DocumentService,
        private state: StateService,
        private file: FileService,
        private modal: ModalService,
        private remember: RememberService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);

        this.selectedProductVersion$.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe((productVersionKeyno) => {
            this.selectedProductVersionKeyno = productVersionKeyno === null ? null : Number(productVersionKeyno);

            if (productVersionKeyno === null) {
                this.productVersionProdplaceGrid = undefined;
                this.cdr.markForCheck();
                return;
            }

            this.readProductVersionProdplaceGrid();
        });
    }

    public async runErrorCheck() {
        if (!this.page) return;

        this.errorCheckRunning = true;

        try {
            const { errorcode, errormessage } = await this.productBom.errorCheck({ prod_id: this.page.prod_id });

            if (!!errorcode && errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error_occured', translateTitle: true, message: errormessage });
            }
        } finally {
            this.errorCheckRunning = false;
            this.cdr.markForCheck();
        }
    }

    public async onProductSelected(event: ProductBomItem[]) {
        this.selectedProductBom = event[0];

        if (!this.selectedProductBom || !this.page) return;

        this.selectedProductBomNodeId = this.selectedProductBom.node_id;
        this.page.prod_id_selected = this.selectedProductBom.item_id;

        await Promise.all([this.remember.remember(`w_productbom.${this.page.prod_id}`, this.page.prod_id_selected), this.remember.remember(this.getSelectedNodeRememberId(), this.selectedProductBomNodeId)]);

        if (!!this.componentsGrid?.config?.toolbar?.toggles?.[0]) {
            if ('' + this.selectedProductBom?.bom_verified === '1') {
                this.componentsGrid.config.toolbar.toggles[0].state = 1;
            } else {
                this.componentsGrid.config.toolbar.toggles[0].state = 0;
            }
        }

        this.componentsGrid?.gridfunc?.read?.();
        this.operationsGrid?.gridfunc?.read?.();

        this.loadUsedInProducts();
        await this.loadSelectedProduct();
        this.loadDocuments();
        this.loadPictures();
        this.loadVideos();
        this.loadVersions();
        this.productVersionsGrid?.gridfunc?.read();
    }

    public onVersionChanged() {
        this.loadProductBomList();
        this.readComponentsGrid();
        this.readOperationsGrid();
        this.componentsGrid?.gridfunc?.read?.();
        this.operationsGrid?.gridfunc?.read?.();
        this.loadUsedInProducts();
    }

    public onProductVersionsGridRead() {
        const gridfunc = this.productVersionsGrid?.gridfunc;

        if ((gridfunc?.getRows().length ?? 0) === 0) {
            this.selectedProductVersion$.next(null);
            return;
        }

        if ((gridfunc?.getGridApi().getSelectedRows().length ?? 0) === 0) gridfunc?.selectRow(0);
    }

    public onProductVersionSelected(event: RowSelectedEvent<ProductVersionRow>) {
        if (!this.page || !event.node.isSelected()) return;

        this.selectedProductVersion$.next(event.data?.productversion_keyno ?? null);
    }

    public onProductVersionsDataChanged(event: RowDataChangeEvent<ProductVersionRow>) {
        if (!event.row) return;

        if (this.sameId(event.row.productversion_keyno, this.selectedProductVersionKeyno)) {
            this.selectedProductVersion$.next(event.row.productversion_keyno ?? null);
        }
    }

    public onTabChanged() {
        if (this.page?.gb_productbom_list === 'COMPONENT') {
            this.readComponentsGrid();
        } else if (this.page?.gb_productbom_list === 'WORK') {
            this.readOperationsGrid();
        } else if (this.page?.gb_productbom_list === 'VERSIONS') {
            this.readProductVersionsGrid();
        }
    }

    public async onAssemblyGroupChanged() {
        if (!!this.page?.assemblygroup_no && !!this.page.prod_id_selected) {
            await this.product.updateProductAssemblyGroup({ prod_id: this.page.prod_id_selected, assemblygroup_no: this.page.assemblygroup_no });
        }
    }

    private async bomVerificationCheck(check: '1' | '0') {
        if (!!this.page && this.selectedProductBom) {
            this.selectedProductBom.bom_verified = check;
            await this.productBom.updateBom({ prod_id: this.page.prod_id_selected, bom_verified: check });
        }
    }

    private readComponentsGrid() {
        if (!this.componentsGrid) {
            this.componentsGrid = new Grid()
                .setRememberId('w_productbom_components')
                .setLoadDataFunction({ method: 2479, parameters: () => ({ prod_id: this.page?.prod_id_selected, productversion_keyno: this.page?.productversion_keyno }) })
                .addToolbarButton({
                    name: 'components_add',
                    text: 'components_add',
                    func: () => {
                        if (!!this.navigator) {
                            this.navigator.go({ to: 'component', parms: `${this.page?.prod_keyno_selected}/0` });
                        } else {
                            this.state.go('component', { prod_id: this.page?.prod_keyno_selected, comp_keyno: '0' });
                        }
                    },
                    icon: 'far fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                    translate: true,
                })
                .addToggleButton({
                    name: 'test',
                    state: '' + this.selectedProductBom?.bom_verified === '1' ? 1 : 0,
                    states: [
                        { text: 'bom_verified_unchecked', func: () => this.bomVerificationCheck('0'), icon: 'fal fa-square' },
                        { text: 'bom_verified_checked', func: () => this.bomVerificationCheck('1'), icon: 'fal fa-check-square' },
                    ],
                    cssClass: 'tt-grid-btn-xs tt-button tt-button--primary',
                });
        } else {
            this.componentsGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    private readOperationsGrid() {
        if (!this.operationsGrid) {
            this.operationsGrid = new Grid()
                .setRememberId('w_productbom_operations')
                .setLoadDataFunction({ method: 2312, parameters: () => ({ prod_id: this.page?.prod_id_selected, productversion_keyno: this.page?.productversion_keyno }) })
                .addToolbarButton({
                    name: 'productbom_work_new',
                    text: 'productbom_work_new',
                    func: () => {
                        if (!!this.navigator) {
                            this.navigator.go({ to: 'work', parms: `${this.page?.prod_keyno_selected}/0` });
                        } else {
                            this.state.go('work', { prod_id: this.page?.prod_keyno_selected, work_keyno: '0' });
                        }
                    },
                    icon: 'far fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                    translate: true,
                })
                .addToolbarButton({
                    name: 'productbom_productionroutingadd',
                    text: 'productbom_productionroutingadd',
                    func: () => {
                        if (!!this.navigator) {
                            this.navigator.go({ to: 'productionroutingadd', parms: `${this.page?.prod_keyno_selected}` });
                        } else {
                            this.state.go('productionroutingadd', { prod_id: this.page?.prod_keyno_selected });
                        }
                    },
                    icon: 'far fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                    translate: true,
                });
        } else {
            this.operationsGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    private readProductVersionsGrid() {
        if (!this.productVersionsGrid) {
            this.productVersionsGrid = new Grid<ProductVersionRow>().setRememberId('w_productbom.productversions.grid').setLoadDataFunction({ method: 4044, parameters: () => ({ prod_id: this.page?.prod_id_selected }) });
        } else {
            this.productVersionsGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    private readProductVersionProdplaceGrid() {
        if (!this.productVersionProdplaceGrid) {
            this.productVersionProdplaceGrid = new Grid()
                .setRememberId('w_productbom.productversion_prodplace.grid')
                .setLoadDataFunction({ method: 4045, parameters: () => ({ productversion_keyno: this.selectedProductVersionKeyno }) });
        } else {
            this.productVersionProdplaceGrid.gridfunc?.read();
        }

        this.cdr.markForCheck();
    }

    private sameId(first?: string | number | null, second?: string | number | null): boolean {
        return `${first ?? ''}` === `${second ?? ''}`;
    }

    private async loadCustomKpis() {
        if (!!this.page) {
            this.customKpis = await this.productBom.getOwnKPIList(this.page);
            this.cdr.markForCheck();
        }
    }

    private async loadProductBomList() {
        if (!!this.page?.prod_id) {
            this.productBomTree = await this.productBom.getProductBomList({ prod_id: this.page.prod_id, productversion_keyno: this.page.productversion_keyno });
            this.cdr.markForCheck();
        }
    }

    private async loadAssemblyGroups() {
        this.assemblyGroups = await this.assemblyGroup.getAssemblyGroups();
        this.cdr.markForCheck();
    }

    private async loadUsedInProducts() {
        this.loadedListTabs.delete('OTHER');
        this.usedInProducts = [];

        try {
            this.usedInProducts = !!this.page?.prod_id_selected ? await this.productBom.getProductsProductIsUsedIn({ prod_id: this.page.prod_id_selected, productversion_keyno: this.page.productversion_keyno }) : [];
        } finally {
            this.loadedListTabs.add('OTHER');
            this.cdr.markForCheck();
        }
    }

    private async loadDocuments() {
        this.loadedListTabs.delete('DOCUMENTS');
        this.documents = [];

        try {
            this.documents = !!this.page?.prod_id_selected ? await this.getProductFiles(this.page.prod_keyno_selected, 'd') : [];
        } finally {
            this.loadedListTabs.add('DOCUMENTS');
            this.cdr.markForCheck();
        }
    }

    private async loadPictures() {
        this.loadedListTabs.delete('PICTURES');
        this.pictures = [];

        try {
            this.pictures = !!this.page?.prod_id_selected ? await this.getProductFiles(this.page.prod_keyno_selected, 'p') : [];
        } finally {
            this.loadedListTabs.add('PICTURES');
            this.cdr.markForCheck();
        }
    }

    private async loadVideos() {
        this.loadedListTabs.delete('VIDEOS');
        this.videos = [];

        try {
            this.videos = !!this.page?.prod_keyno_selected ? await this.getProductFiles(this.page.prod_keyno_selected, 'v') : [];
        } finally {
            this.loadedListTabs.add('VIDEOS');
            this.cdr.markForCheck();
        }
    }

    private async loadVersions() {
        this.versions = [];

        if (!!this.page) {
            this.versions = await this.product.getProductVersions({ prod_id: this.page?.prod_id_selected });
        }

        this.cdr.markForCheck();
    }

    private async loadPage() {
        if (!!this.params) {
            const get = await this.productBom.getProductBom(this.params);

            if (!!get) {
                const selectedProduct = await this.productBom.getSelectedProduct({ prod_id: get.prod_id, prod_id_selected: get.prod_id_selected });

                this.page = { ...get, ...selectedProduct };
                this.cdr.detectChanges();
                this.loadSelectedProductImage();
            }
        }
    }

    private getSelectedNodeRememberId(): string {
        return `w_productbom.treeview.selected-node.${this.page?.prod_id ?? ''}`;
    }

    private async setSelectedProductBom() {
        if (!this.page) return;

        let rememberedNodeId = '';

        try {
            rememberedNodeId = '' + (await this.remember.getLastStatus(this.getSelectedNodeRememberId()))[0]?.variablevalue;
        } catch {}

        this.selectedProductBom = this.productBomTree.find((item) => item.node_id === rememberedNodeId && item.item_id === this.page?.prod_id_selected) ?? this.productBomTree.find((item) => item.item_id === this.page?.prod_id_selected);
        this.selectedProductBomNodeId = this.selectedProductBom?.node_id ?? '';
        this.cdr.markForCheck();
    }

    public async loadSelectedProduct() {
        if (!!this.page?.prod_id_selected) {
            this.page = { ...this.page, ...(await this.productBom.getSelectedProduct({ prod_id: this.page.prod_id, prod_id_selected: this.page.prod_id_selected })) };
            this.cdr.markForCheck();
            this.loadSelectedProductImage();
        }
    }

    private async loadSelectedProductImage() {
        if (!!this.page?.product_main_image && typeof this.page.product_main_image === 'string') {
            try {
                const result = await this.file.getFileInfo({ selectOnlyFromFiles: true, files: [{ fileName: this.page.product_main_image }] });

                if (typeof result === 'object' && 'files' in result && result.files.length > 0) {
                    const thumbUrl = result.files[0].thumbUrl;

                    // const decoded = decodeURIComponent(thumbUrl);
                    // const queryIndex = decoded.indexOf('{');
                    // const url = decoded.substring(0, queryIndex);
                    // let queries = decoded.substring(queryIndex);

                    // queries = queries.substring(0, queries.indexOf(',"width"')) + ',"width":133,"height":100}';
                    // queries = encodeURIComponent(queries);

                    // this.productImageUrl = url + queries;
                    this.productImageUrl = thumbUrl;
                } else {
                    this.productImageUrl = '';
                }
            } catch (error) {
                console.error(error);
            }
        } else {
            this.productImageUrl = '';
        }

        this.cdr.markForCheck();
    }

    private async init() {
        this.loadAssemblyGroups();

        await this.loadPage();
        this.navigator?.setHeading?.(this.page?.webpage_heading ?? '');

        const productBomList = this.loadProductBomList();
        this.loadUsedInProducts();
        this.loadDocuments();
        this.loadPictures();
        this.loadVideos();
        this.loadVersions();
        this.loadCustomKpis();

        await productBomList;
        await this.setSelectedProductBom();
        this.onTabChanged();
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    /**
     * Retrieves files related to the given product id of the given filetype.
     *
     * @param prod_id The id of the product to retrieve files related to.
     * @param filetype The type of faile to retrieve, `'d'` for document (pdf, .docx), `'p'` for pictures and `'v'` for videos.
     */
    private getProductFiles(prod_id: string, filetype: 'd' | 'p' | 'v') {
        return this.document.getDocuments({ argtype: 'prod_id', argvalue: prod_id, searchtext: '', doctype: filetype, onlyFilenames: '0' });
    }
}

type ProductVersionRow = GridRow & ProductVersionItem;

type ProductBomListTab = 'OTHER' | 'DOCUMENTS' | 'PICTURES' | 'VIDEOS' | 'VERSIONS';

// (function () {
//     'use strict';

//     const module = angular.module('imApp');

//     module.component('productBom', {
//         templateUrl: 'views/components/views/productBom/productBom.template.html?v=' + module.version,
//         controllerAs: 'vm',
//         controller: ['$stateParams', 'stateService', 'utilityService', 'translateService', 'modalService', 'productBomService', 'ttGridFactory', 'rememberService', 'fileService', function ($stateParams, stateService, utilityService, translateService, modalService, productBomService, ttGridFactory, rememberService, fileService) {

//             // #region VARIABLES & DEFINITIONS

//             const vm = this;
//             let prodId = $stateParams.prod_id;

//             let selectedProductBom = {
//                 bom_verified: '0'
//             };

//             let variableNames = {
//                 prod_id_selected: '',
//                 gb_productbom_list: ''
//             };

//             let translations = {
//                 error: '',
//                 ok: ''
//             };

//             let style = {
//                 container: {},
//                 img: {},
//                 placeholder: {}
//             };

//             vm.model = {
//                 prodId: prodId,
//                 gridComponentsReady: false,
//                 gridOperationsReady: false,
//                 bgProductBomList: {
//                     selected: 'COMPONENT',
//                     buttons: [
//                         { id: 'COMPONENT', label: 'gb_productbom_list_component', color: 'primary', onClick: () => setProductBomListView('COMPONENT') },
//                         { id: 'WORK', label: 'gb_productbom_list_work', color: 'primary', onClick: () => setProductBomListView('WORK') },
//                         { id: 'OTHER', label: 'gb_productbom_list_other', color: 'primary', onClick: () => setProductBomListView('OTHER') },
//                         { id: 'DOCUMENTS', label: 'documents_documents', color: 'primary', onClick: () => setProductBomListView('DOCUMENTS') },
//                         { id: 'PICTURES', label: 'documents_pictures', color: 'primary', onClick: () => setProductBomListView('PICTURES') },
//                         { id: 'VIDEOS', label: 'documents_videos', color: 'primary', onClick: () => setProductBomListView('VIDEOS') }
//                     ]
//                 },
//                 productSelectedButtons: [{ id: 'goto', icon: 'fa fa-chevron-right', color: 'primary', type: 'primary', onClick: () => stateService.go('product', { prod_id: vm.model.setting.prod_keyno_selected }) }],
//                 setting: {},
//                 treeListProductBoms: [],
//                 itemsListIncludedIn: [],
//                 selectListAssemblyGroups: [],
//                 itemsListOwns: [],
//                 itemsListDocuments: [],
//                 itemsListPictures: [],
//                 itemsListVideos: [],
//                 checkingError: false
//             };

//             vm.gridComponents = new ttGridFactory({
//                 rememberId: 'w_productbom_components',
//                 kendo: {
//                     height: '700px',
//                     sortable: true,
//                     aggregate: true,
//                     pager: false,
//                     selectable: false,
//                     filterable: false
//                 }
//             }).setCustomToolbarButtons([{
//                 name: 'components_add',
//                 text: 'components_add',
//                 func: () => {
//                     stateService.go('component', {
//                         prod_id: vm.model.setting.prod_keyno_selected,
//                         comp_keyno: '0'
//                     });
//                 },
//                 icon: 'fa-plus',
//                 cssClass: 'btn btn-primary im-grid-btn-xs-r',
//                 translate: true
//             }]).setToggleButtons([{
//                 name: 'test',
//                 text: 'test',
//                 state: 0,
//                 states: [
//                     { text: 'bom_verified_unchecked', func: function () { verifyBom(0) }, icon: 'fal fa-square' },
//                     { text: 'bom_verified_checked', func: function () { verifyBom(1) }, icon: 'fal fa-check-square' }
//                 ],
//                 cssClass: 'tt-grid-btn-xs tt-button--primary',
//                 icon: 'fal fa-ballot-check',
//                 disabled: function () { return false; }
//             }]);

//             vm.gridOperations = new ttGridFactory({
//                 rememberId: 'w_productbom_operations',
//                 kendo: {
//                     height: '700px',
//                     sortable: true,
//                     aggregate: true,
//                     pager: false,
//                     selectable: false,
//                     filterable: false
//                 }
//             }).setCustomToolbarButtons([
//                 {
//                     name: 'productbom_work_new',
//                     text: 'productbom_work_new',
//                     func: () => {
//                         stateService.go('work', {
//                             prod_id: vm.model.setting.prod_keyno_selected,
//                             work_keyno: '0'
//                         });
//                     },
//                     icon: 'fa-plus',
//                     cssClass: 'btn btn-primary im-grid-btn-xs-r',
//                     translate: true
//                 },
//                 {
//                     name: 'productbom_productionroutingadd',
//                     text: 'productbom_productionroutingadd',
//                     func: () => stateService.go('productionroutingadd', { prod_id: vm.model.setting.prod_keyno_selected }),
//                     icon: 'fa-plus',
//                     cssClass: 'btn btn-primary im-grid-btn-xs-r',
//                     translate: true
//                 }
//             ]);

//             // #endregion VARIABLES & DEFINITIONS

//             // #region TRANSLATION FUNCTION

//             async function translate() {
//                 let data = await translateService.translateBatch(translations);

//                 Object.keys(translations).forEach((key) => {
//                     if (data?.[key]) translations[key] = data[key];
//                 });
//             }

//             // #endregion TRANSLATION FUNCTION

//             // #region LOAD PROCEDURE CALLS

//             async function loadSetting() {
//                 vm.model.setting = (await productBomService.getSetting({ prod_id: prodId }))[0];
//             }

//             async function loadAssemblyGroups() {
//                 vm.model.selectListAssemblyGroups = await productBomService.listAssemblyGroups();
//             }

//             async function loadOwns() {
//                 return vm.model.itemsListOwns = await productBomService.listOwns(vm.model.setting);
//             }

//             async function loadIncludedIn() {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 return vm.model.itemsListIncludedIn = await productBomService.listIncludedIn({ prod_id: vm.model.setting.prod_id_selected });
//             }

//             async function loadProductBoms() {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id) !== true) return;

//                 return vm.model.treeListProductBoms = await productBomService.listProductBoms({ prod_id: vm.model.setting.prod_id });

//                 selectedProductBom = vm.model.treeListProductBoms.find(item => item.item_id === vm.model.setting.prod_id_selected);

//                 if (selectedProductBom?.bom_verified && (selectedProductBom.bom_verified === '1' || selectedProductBom.bom_verified === 1)) vm.gridComponents.config.toolbar.toggles[0].state = 1;
//                 else vm.gridComponents.config.toolbar.toggles[0].state = 0;
//             }

//             async function loadDocuments(id) {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_keyno_selected) !== true) return;

//                 switch (id) {
//                     case 'DOCUMENTS':
//                         vm.model.itemsListDocuments = await productBomService.listDocuments({
//                             argtype: 'prod_id',
//                             argvalue: vm.model.setting.prod_keyno_selected,
//                             searchtext: '',
//                             doctype: 'd',
//                             onlyFilenames: '0',
//                         });
//                         break;
//                     case 'PICTURES':
//                         vm.model.itemsListPictures = await productBomService.listDocuments({
//                             argtype: 'prod_id',
//                             argvalue: vm.model.setting.prod_keyno_selected,
//                             searchtext: '',
//                             doctype: 'p',
//                             onlyFilenames: '0',
//                         });
//                         break;
//                     case 'VIDEOS':
//                         vm.model.itemsListVideos = await productBomService.listDocuments({
//                             argtype: 'prod_id',
//                             argvalue: vm.model.setting.prod_keyno_selected,
//                             searchtext: '',
//                             doctype: 'v',
//                             onlyFilenames: '0',
//                         });
//                         break;
//                     default:
//                         break;
//                 }
//             }

//             async function retrieveProdIdSelected() {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 const response = (await productBomService.getProdIdSelected({ prod_id_selected: vm.model.setting.prod_id_selected }))[0];

//                 vm.model.setting.prod_name_selected = response?.prod_name_selected;
//                 vm.model.setting.prod_keyno_selected = response?.prod_keyno_selected;
//                 vm.model.setting.url_picture = response?.url_picture;
//                 vm.model.setting.product_main_image = response?.product_main_image;
//                 vm.model.setting.assemblygroup_id = response?.assemblygroup_no;

//                 // Load product image
//                 let productMainImage = vm.model.setting.product_main_image;

//                 if (typeof (productMainImage) === 'string' && productMainImage !== '') {
//                     fileService.getFileInfo({
//                         selectOnlyFromFiles: true,
//                         files: [{
//                             "fileName": productMainImage
//                         }]//,
//                         //page: 1,
//                         //pageSize: 1
//                     }).then(function (result) {
//                         if (typeof (result) === 'object' && "files" in result && result.files.length > 0) {
//                             // We only want first image for the first version of this feature
//                             var fileData = result.files[0];
//                             var thumbUrl = fileData.thumbUrl;

//                             var decoded = decodeURIComponent(thumbUrl);
//                             var qsIndex = decoded.indexOf('{');
//                             var url = decoded.substr(0, qsIndex);
//                             var qs = decoded.substr(qsIndex);

//                             qs = qs.substr(0, qs.indexOf(',"width"')) + ',"width":133,"height":100}';
//                             qs = encodeURIComponent(qs);

//                             // Insert link to image for use in HTML
//                             vm.model.setting.productImageUrl = url + qs;
//                         }
//                     });
//                 }
//             }

//             function verifyBom(state) {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 selectedProductBom.bom_verified = state;

//                 return productBomService.updateBom({
//                     prod_id: vm.model.setting.prod_id_selected,
//                     bom_verified: state
//                 });
//             }

//             function updateAssemblyGroup(assemblyGroupId) {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 vm.model.setting.assemblygroup_id = assemblyGroupId;

//                 return productBomService.saveAssemblyGroup({
//                     prod_id: vm.model.setting.prod_id_selected,
//                     assemblygroup_no: assemblyGroupId
//                 });
//             }

//             // #endregion LOAD PROCEDURE CALLS

//             // #region GRID PROCEDURE FUNCTION CALLS

//             async function loadGridComponents() {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 vm.gridComponents.dataTask.loadData = {
//                     method: 2479,
//                     parameters: { prod_id: vm.model.setting.prod_id_selected }
//                 };

//                 if (vm.model.bgProductBomList.selected === 'COMPONENT') vm.model.gridComponentsReady = true;

//                 if (vm.gridComponents.gridfunc !== null) vm.gridComponents.gridfunc.read();
//             }

//             async function loadGridOperations() {
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) !== true) return;

//                 vm.gridOperations.dataTask.loadData = {
//                     method: 2312,
//                     parameters: { prod_id: vm.model.setting.prod_id_selected }
//                 };

//                 if (vm.model.bgProductBomList.selected === 'WORK') vm.model.gridOperationsReady = true;
//                 if (vm.gridOperations.gridfunc !== null) vm.gridOperations.gridfunc.read();
//             }

//             // #endregion GRID PROCEDURE FUNCTION CALLS

//             // #region REMEMBER VALUE MULTI PROCEDURE FUNCTION CALLS

//             function rememberFunc(id) {
//                 Object.keys(variableNames).forEach((key) => {
//                     switch (key) {
//                         case 'prod_id_selected':
//                             variableNames[key] = 'w_' + stateService.getCurrentName() + '.' + vm.model.setting.prod_id;
//                             break;
//                         case 'gb_productbom_list':
//                             variableNames[key] = 'w_' + stateService.getCurrentName() + '.' + 'gb_productbom_list' + '.' + vm.model.setting.prod_id;
//                             break;
//                         default:
//                             variableNames[key] = 'w_' + stateService.getCurrentName() + '.' + key;
//                             break;
//                     }
//                 });

//                 if (utilityService.validateParmsValue(variableNames[id]) !== true) return;

//                 let variableValue = null;

//                 switch (id) {
//                     case 'prod_id_selected':
//                     case 'gb_productbom_list':
//                         if (utilityService.validateParmsValue(vm.model.setting[id]) !== true) return;

//                         variableValue = vm.model.setting[id];
//                         break;
//                     default:
//                         break;
//                 }

//                 return rememberService.remember(variableNames[id], variableValue);
//             }

//             // #endregion REMEMBER VALUE MULTI PROCEDURE FUNCTION CALLS

//             // #region CHECK BUTTON FUNCTION

//             vm.checkError = function () {
//                 vm.model.checkingError = true;

//                 productBomService.checkProduct({ prod_id: vm.model.setting.prod_id }).then(function (response) {
//                     if (response[0].errorcode !== '0') {
//                         modalService.show({
//                             type: 'warning',
//                             title: translations.error,
//                             message: response[0].errormessage,
//                             buttons: [{
//                                 label: translations.ok,
//                                 cssClass: 'btn-warning',
//                                 action: function (dialogItself) {
//                                     dialogItself.close();
//                                     vm.model.checkingError = false;
//                                 }
//                             }]
//                         });
//                     } else {
//                         vm.model.checkingError = false;
//                     }
//                 });
//             }

//             // #endregion CHECK BUTTON FUNCTION

//             // #region SET IMAGE HEIGHT FUNCTION

//             function setImageHeight() {
//                 setTimeout(() => {
//                     let fieldsDiv = document.querySelector('.productbom__fields');
//                     let firstChild = fieldsDiv?.children?.[0];

//                     if (firstChild) {
//                         style.container.marginBottom = '0';
//                         style.img.minHeight = `70px`;
//                         style.placeholder.minHeight = `70px`;
//                         style.img.height = `${firstChild.clientHeight - (10.111 * 4 + 7)}px`;
//                         style.placeholder.height = `${firstChild.clientHeight - (10.111 * 4 + 7)}px`;

//                         style = { ...style };
//                     }
//                 }, 50);
//             }

//             // #endregion SET IMAGE HEIGHT FUNCTION

//             // #region BUTTON GROUP SINGLE FUNCTION

//             async function setProductBomListView(id) {
//                 vm.model.bgProductBomList.selected = vm.model.setting.gb_productbom_list = id;
//             }

//             // #endregion BUTTON GROUP SINGLE FUNCTION

//             // #region BUTTON GROUP FUNCTIONS

//             vm.selectBgProductBomList = async function (item) {
//                 vm.model.bgProductBomList.selected = item;
//                 vm.model.setting.gb_productbom_list = vm.model.bgProductBomList.selected;

//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) === true && vm.model.bgProductBomList.selected === 'COMPONENT') vm.model.gridComponentsReady = true;
//                 if (utilityService.validateParmsValue(vm.model.setting.prod_id_selected) === true && vm.model.bgProductBomList.selected === 'WORK') vm.model.gridOperationsReady = true;

//                 await rememberFunc('gb_productbom_list');
//                 if (vm.model.bgProductBomList.selected === 'DOCUMENTS' || vm.model.bgProductBomList.selected === 'PICTURES' || vm.model.bgProductBomList.selected === 'VIDEOS') loadDocuments(vm.model.bgProductBomList.selected);
//             }

//             // #endregion BUTTON GROUP FUNCTIONS

//             // #region TREE SELECTION FUNCTION

//             vm.selectTreeItem = async function (item) {
//                 selectedProductBom = item;
//                 vm.model.setting.prod_id_selected = item.item_id;

//                 if (selectedProductBom?.bom_verified && (selectedProductBom.bom_verified === '1' || selectedProductBom.bom_verified === 1)) vm.gridComponents.config.toolbar.toggles[0].state = 1;
//                 else vm.gridComponents.config.toolbar.toggles[0].state = 0;

//                 await Promise.all([rememberFunc('prod_id_selected'), retrieveProdIdSelected()]);
//                 loadGridComponents();
//                 loadGridOperations();
//                 loadIncludedIn();
//                 setImageHeight();

//                 if (vm.model.bgProductBomList.selected === 'COMPONENT' && vm.model.gridComponentsReady === true) loadGridComponents();
//                 if (vm.model.bgProductBomList.selected === 'WORK' && vm.model.gridOperationsReady === true) loadGridOperations();
//                 if (vm.model.bgProductBomList.selected === 'DOCUMENTS' || vm.model.bgProductBomList.selected === 'PICTURES' || vm.model.bgProductBomList.selected === 'VIDEOS') loadDocuments(vm.model.bgProductBomList.selected);
//             }

//             // #endregion TREE SELECTION FUNCTION

//             // #region FIELD CHANGE MULTI FUNCTIONS

//             vm.onChanged = function (value, id) {
//                 switch (id) {
//                     case 'assemblygroup_id':
//                         if (utilityService.validateWatchValue(value, vm.model.setting.assemblygroup_id) !== true) return;

//                         vm.model.setting.assemblygroup_id = value;

//                         if (utilityService.validateParmsValue(vm.model.setting.assemblygroup_id) !== true) return;

//                         updateAssemblyGroup(vm.model.setting.assemblygroup_id);
//                         break;
//                     default:
//                         utilityService.setValueToVariable(value);
//                         break;
//                 }
//             };

//             // #endregion FIELD CHANGE MULTI FUNCTIONS

//             // #region ON INIT FUNCTION

//             vm.$onInit = async function () {
//                 translate();
//                 await loadSetting();
//                 await Promise.all([setProductBomListView(vm.model.setting.gb_productbom_list), loadProductBoms()]);
//                 loadGridOperations();
//                 loadGridComponents();
//                 loadIncludedIn();
//                 loadOwns();
//                 retrieveProdIdSelected();
//                 loadAssemblyGroups();

//                 if (vm.model.bgProductBomList.selected === 'DOCUMENTS' || vm.model.bgProductBomList.selected === 'PICTURES' || vm.model.bgProductBomList.selected === 'VIDEOS') loadDocuments(vm.model.bgProductBomList.selected);

//                 setImageHeight();
//             }

//             // #endregion ON INIT FUNCTION

//         }]
//     });
// })();
